import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';
// import AUDIT_REQUEST_SIZE
import { AUDIT_REQUEST_SIZE } from '../helpers/constants';
import { killshotApi } from '../api';

const fetchAuditTrailInfo = async (eventId = '') => {
  const response = await killshotApi.get(`v1/journal/${eventId}`);
  return response;
};

const fetchAuditTrailInfoPagination = async (eventId = '', offset = 0) => {
  const response = await killshotApi.get(`v1/journal/${eventId}?page=${offset}&limit=${AUDIT_REQUEST_SIZE}`);
  return {
    items: response.items,
    nextPage: response.next_page ? offset + 1 : undefined,
  };
};

const fetchAutomationAuditTrailInfo = async (eventId = '') => {
  const response = await killshotApi.get(`v1/automation/audit/${eventId}`);
  return response;
};

const fetchAuditTrailViews = async (eventId = '') => {
  const response = await killshotApi.get(`v1/journal/${eventId}/journal_views`);
  return response;
};

const updateReadJournalRecords = async data => {
  const url = `v1/journal/${data.eventId}/journal_views`;
  await killshotApi.post(url, {
    journal_ids: data.journalIds,
  });
};

export const useQueryAuditTrailViews = eventId => {
  const { data, isFetching, error, status } = useQuery({
    queryKey: [`/journal/${eventId}/views`],
    queryFn: () => fetchAuditTrailViews(eventId),
    refetchOnWindowFocus: false,
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 2 * 60 * 1000,
  });
  return {
    data,
    isFetching,
    status,
    error,
  };
};

export const useQueryAuditTrailInfo = eventId => {
  const { data, isFetching, error, status } = useQuery({
    queryKey: [`/journal/${eventId}`],
    queryFn: () => fetchAuditTrailInfo(eventId),
    refetchOnWindowFocus: false,
    enabled: !!eventId,
    staleTime: 0,
    refetchOnMount: true,
  });
  return {
    data,
    isFetching,
    status,
    error,
  };
};

export const useQueryAuditTrailInfoPagination = eventId => {
  const { data, isFetching, error, status, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [`/journal/${eventId}`],
    queryFn: ({ pageParam = 0 }) => {
      const offset = pageParam || 0;
      return fetchAuditTrailInfoPagination(eventId, offset);
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      return lastPage.nextPage;
    },
    refetchOnWindowFocus: false,
    enabled: !!eventId,
    staleTime: 0,
    refetchOnMount: true,
  });
  return {
    data,
    isFetching,
    status,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};

export const useQueryAutomationAuditInfo = eventId => {
  const { data, isFetching, error, status } = useQuery({
    queryKey: [`/automation/audit/${eventId}`],
    queryFn: () => fetchAutomationAuditTrailInfo(eventId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
  });
  return {
    data,
    isFetching,
    status,
    error,
  };
};

export const postJournalViews = () => {
  const { mutate } = useMutation({
    mutationFn: data => updateReadJournalRecords(data),
  });
  return {
    mutate,
  };
};
